import { useState } from "react"

import { useSearchDocumentParams } from "../../useDocumentList"
import { getFilterType } from "./utils"

export type FilterType = "customType" | "tag" | "author"

export interface Filter {
  value: string
  type: FilterType
}

export function useActionBar() {
  const { searchDocumentParams, updateSearchDocumentParams } =
    useSearchDocumentParams()

  function initializeFilters() {
    const customTypes = getFiltersFromParams(
      searchDocumentParams.customTypes,
      "customType"
    )

    const tags = getFiltersFromParams(searchDocumentParams.tags, "tag")
    const authors = getFiltersFromParams(searchDocumentParams.authors, "author")

    return [...customTypes, ...tags, ...authors]
  }

  const [selectedFilters, setSelectedFilters] =
    useState<Filter[]>(initializeFilters)

  function setFilterSearchParams(newValues: Filter[], filterType: FilterType) {
    switch (filterType) {
      case "customType": {
        const customTypesFilters = getFilterType(newValues, "customType")

        return updateSearchDocumentParams({
          customTypes: customTypesFilters.map((filter) => filter.value)
        })
      }
      case "tag": {
        const tagFilters = getFilterType(newValues, "tag")

        return updateSearchDocumentParams({
          tags: tagFilters.map((filter) => filter.value)
        })
      }
      case "author": {
        const authorFilters = getFilterType(newValues, "author")

        return updateSearchDocumentParams({
          authors: authorFilters.map((filter) => filter.value)
        })
      }
    }
  }

  function onRemoveFilter(filterId: string, filterType: FilterType) {
    const newValues = selectedFilters?.filter(
      (filter) => filter.value !== filterId || filter.type !== filterType
    )

    setSelectedFilters(newValues)
    setFilterSearchParams(newValues, filterType)
  }

  function onFilterSelected(newFilterId: string, filterType: FilterType) {
    const alreadySelected = selectedFilters?.some(
      (type) => type.value === newFilterId && type.type === filterType
    )

    if (alreadySelected) return onRemoveFilter(newFilterId, filterType)

    const newFilters = [
      ...selectedFilters,
      { value: newFilterId, type: filterType }
    ]

    setSelectedFilters(newFilters)
    setFilterSearchParams(newFilters, filterType)
  }

  return {
    selectedFilters,
    onRemoveFilter,
    onFilterSelected
  }
}

function getFiltersFromParams(
  params: string[] | undefined,
  type: FilterType
): Filter[] {
  if (!params) return []

  return params.map((param) => ({
    value: param,
    type
  }))
}
